import { useNavigate } from '@tanstack/react-router'
import { useEffect } from 'react'

import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  SelectField,
  TextField,
} from '@/components/ui'
import { useToast } from '@/hooks'
import {
  type Document,
  useDocument,
  useDocumentActions,
  useDocumentMutating,
  useDocumentMutations,
} from '@/modules/documents'
import {
  useFolders,
  useFoldersLoaders,
  useLoadingFolders,
} from '@/modules/folders'
import {
  useDialogActions,
  useDialogData,
  useDialogIsOpen,
  useForm,
} from '@/stores'
import { getDocumentPath } from '@/utils'

const dialogId = 'edit-document'

export const EditDocumentDialog = () => {
  const { successToast, errorToast } = useToast()
  const navigate = useNavigate()
  const open = useDialogIsOpen(dialogId)
  const dialogData = useDialogData() as Document
  const { closeDialog } = useDialogActions()
  const { data, setFieldValue } = useForm()

  const document = useDocument()
  const { setDocument } = useDocumentActions()
  const { updateDocument } = useDocumentMutations()
  const { fetchFolders } = useFoldersLoaders()
  const folders = useFolders()
  const isLoadingFolders = useLoadingFolders()
  const isUpdating = useDocumentMutating()

  useEffect(() => {
    fetchFolders('document')
  }, [])

  useEffect(() => {
    if (!dialogData || !open) return
    Object.keys(dialogData).forEach((field) => {
      setFieldValue(field, dialogData[field as keyof Document] as string)
    })
  }, [open, dialogData])

  useEffect(() => {
    if (!open || document || !dialogData?.id) {
      return
    }
    setDocument(dialogData)
  }, [open, document, dialogData])

  const onSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    const { folderId } = data as Partial<Document>

    updateDocument({ folder_id: folderId })
      .then((document) => {
        closeDialog(true)
        successToast('Updated document successfully')
        navigate({ to: getDocumentPath(document) })
      })
      .catch((error) => errorToast('Failed to update document', error))
  }

  return (
    <Dialog open={open} onOpenChange={(open) => !open && closeDialog()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Edit Document</DialogTitle>
          <DialogDescription>
            Enter a changes for the document "{document?.fileName}".
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={onSubmit}>
          <div className="py-4 space-y-4">
            <div className="space-y-6">
              <SelectField
                label="Folder"
                id="folder-name"
                placeholder="Select a folder"
                value={`${data?.folderId}`}
                onValueChange={(value: string) => {
                  setFieldValue('folderId', value)
                }}
                isLoading={isLoadingFolders}
                options={[
                  ...folders.map((folder) => ({
                    label: folder.name,
                    value: `${folder.id}`,
                  })),
                ]}
              />
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <Button theme="outline" onClick={() => closeDialog(false)}>
              Cancel
            </Button>
            <Button type="submit" isLoading={isUpdating}>
              Edit Document
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  )
}

EditDocumentDialog.id = dialogId
